/**
 * ChartContainer — Root chart card component.
 *
 * Wraps each chart in a Fluent UI Card with:
 * - Title header
 * - Edit/duplicate/delete menu
 * - Error boundary
 * - Loading/error/empty states
 * - Lazy rendering via IntersectionObserver
 *
 * Supports two data modes:
 * 1. Self-fetch (dashboard) — provide dashboardId + filters + filtersReady
 * 2. Prop-driven (edit preview) — provide chartData + isLoading + error
 *
 * Renders all ECharts chart types via ChartRenderer.
 * KPI and Table use dedicated React components.
 */
import React, { useMemo } from 'react';
import {
  Card,
  CardPreview,
  Body1,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MoreHorizontalRegular,
  EditRegular,
  CopyRegular,
  DeleteRegular,
  ImageRegular,
} from '@fluentui/react-icons';
import { useVisibility, useChartData } from '../hooks';
import type { Chart as ApiChart, ChartType } from '../../../api/types';
import type { ChartData } from '../echarts/optionBuilders';
import { type ChartEditorState, buildEditorState, getPaletteColors, getCapabilities } from '../../edit/visualConfig';

// Chart renderers
import ChartRenderer from '../echarts/ChartRenderer';
import StatCard from '../ui/StatCard';
import TableChart from '../ui/TableChart';

// ── Error boundary ─────────────────────────────────────────────────────────

interface ChartErrorBoundaryProps {
  title: string;
  chartKey: string;
  children: React.ReactNode;
}

interface ChartErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
}

class ChartErrorBoundary extends React.Component<ChartErrorBoundaryProps, ChartErrorBoundaryState> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error: Error): ChartErrorBoundaryState {
    return { hasError: true, errorMessage: error.message || 'Unknown error' };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Chart error in ${this.props.chartKey}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>{this.props.title}</MessageBarTitle>
            Error rendering chart: {this.state.errorMessage}
          </MessageBarBody>
        </MessageBar>
      );
    }
    return this.props.children;
  }
}

// ── Props ──────────────────────────────────────────────────────────────────

interface ChartContainerProps {
  chartType: ChartType;
  chartConfig: ApiChart;
  isDark?: boolean;
  onEdit?: (chartId: string) => void;
  onDuplicate?: (chartId: string) => void;
  onDelete?: (chartId: string) => void;
  onSetAsThumbnail?: (chartId: string) => void;
  className?: string;
  /** Enable animation (default off for dashboard, on for edit preview) */
  animated?: boolean;

  // Self-fetch mode (dashboard)
  dashboardId?: string;
  filters?: Record<string, any>;
  filtersReady?: boolean;
  /** Regenerate SQL from field_mappings at query time */
  recompile?: boolean;
  shareToken?: string;
  sharedDashboardId?: string;

  // Prop-driven mode (edit preview)
  chartData?: ChartData | undefined;
  isLoading?: boolean;
  error?: string | null;

  /** Editor state for live preview overrides (edit mode only) */
  editorState?: ChartEditorState;

  /** Hide the card header / title (e.g. in edit preview where title is in TopBar) */
  hideTitle?: boolean;

  /** Dashboard-level theme — used as fallback palette when chart has no custom palette */
  dashboardTheme?: string;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  cardWrapper: {
    position: 'relative',
    ':hover .edit-button': {
      opacity: 1,
    },
  },
  editableCard: {
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  editButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    zIndex: 2,
    ':focus': {
      opacity: 1,
    },
  },
  chartActions: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    zIndex: 2,
  },
});

const EMPTY_FILTERS: Record<string, any> = {};

// ── Component ──────────────────────────────────────────────────────────────

export const ChartContainer: React.FC<ChartContainerProps> = ({
  chartType,
  chartConfig,
  isDark = false,
  onEdit,
  onDuplicate,
  onDelete,
  onSetAsThumbnail,
  className = 'chart-container',
  animated = false,
  dashboardTheme,
  dashboardId,
  filters,
  filtersReady = true,
  chartData: propChartData,
  isLoading: _propIsLoading = false,
  recompile = false,
  shareToken,
  sharedDashboardId,
  error: propError = null,
  editorState,
  hideTitle = false,
}) => {
  const styles = useStyles();
  const chartKey = chartConfig.id;
  const title = chartConfig.title;

  // Lazy rendering: defer ECharts init until near viewport
  const { ref: visibilityRef, isVisible } = useVisibility<HTMLDivElement>();

  // Self-fetch mode
  const isSelfFetch = !!dashboardId;
  const selfFetchState = useChartData(
    dashboardId,
    chartKey,
    filters ?? EMPTY_FILTERS,
    isVisible,
    filtersReady,
    recompile,
    shareToken,
    sharedDashboardId,
  );

  // Resolve effective data/error
  const chartData = (isSelfFetch ? selfFetchState.data : propChartData) as ChartData | undefined;
  const error = isSelfFetch ? selfFetchState.error : propError;

  // Build effective visual config: use prop in edit mode, or rebuild from
  // saved chartStyle + chartFieldsConfig in dashboard mode so persisted styles apply.
  // Memoized to prevent expensive option rebuilds on every re-render.
  const isEditMode = !!editorState;
  const effectiveEditorState = useMemo(() => {
    if (editorState) return editorState;
    const built = buildEditorState(
      chartConfig.chartType ?? 'bar',
      chartConfig.chartStyle as Record<string, unknown> | null,
      chartConfig.chartFieldsConfig as Record<string, unknown> | null,
    );
    // Apply dashboard theme as fallback: only if the chart has no
    // persisted palette (i.e. the user never customized it).
    const stored = chartConfig.chartStyle as Record<string, unknown> | null;
    const hasCustomPalette = stored && 'palette' in stored;
    if (!hasCustomPalette && dashboardTheme) {
      built.chartStyle = { ...built.chartStyle, palette: dashboardTheme as typeof built.chartStyle.palette };
    }
    return built;
  }, [editorState, chartConfig.chartType, chartConfig.chartStyle, chartConfig.chartFieldsConfig, dashboardTheme]);

  // Effective chart type: in edit mode, use the editor's chartType so the
  // preview updates live when the user switches chart type.
  const effectiveChartType = isEditMode && effectiveEditorState
    ? effectiveEditorState.chartType
    : chartType;

  // Render the chart content based on type.
  const renderChartContent = () => {
    if (!chartData) return null;

    switch (effectiveChartType) {
      case 'kpi': {
        // Priority: per-series color override > palette first color.
        // Key-agnostic: grab the first override value since KPI has exactly one series.
        const seriesColorMap = effectiveEditorState?.chartStyle?.seriesColors;
        const seriesOverride = seriesColorMap
          ? Object.values(seriesColorMap)[0]
          : undefined;
        const palette = effectiveEditorState?.chartStyle?.palette ?? 'default';
        const kpiColor = seriesOverride || getPaletteColors(palette, isDark)[0];
        return (
          <StatCard
            data={chartData}
            title={title}
            trend={chartData?.trend ?? undefined}
            typeStyle={effectiveEditorState?.chartStyle?.typeStyle}
            paletteColor={kpiColor}
          />
        );
      }

      case 'table':
        return <TableChart data={chartData} />;

      default:
        // All other chart types rendered via ECharts
        return (
          <ChartRenderer
            data={chartData}
            isDark={isDark}
            animated={animated}
            editorState={effectiveEditorState}
            isEditMode={isEditMode}
            fieldMappings={chartConfig.fieldMappings}
            chartId={chartKey}
          />
        );
    }
  };

  // Resolve loading state (self-fetch or prop-driven)
  const isLoading = isSelfFetch ? selfFetchState.isLoading : _propIsLoading;

  const renderCardBody = () => {
    if (error) {
      return (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>{title}</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      );
    }

    if (chartData) {
      // Data was fetched but the result set is empty — show a "No data" message
      // instead of passing empty arrays to ECharts (which would leave old data visible).
      const hasRows = chartData.rows && chartData.rows.length > 0;
      const hasCols = chartData.columns && chartData.columns.length > 0;
      if (!hasRows || !hasCols) {
        if (isLoading) {
          return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Spinner />
            </div>
          );
        }
        return (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--colorNeutralForeground3)' }}>
            <Body1>No data available</Body1>
          </div>
        );
      }

      // Show chart with current data. If refetching, overlay a subtle opacity
      // change instead of unmounting the chart (prevents flicker).
      return (
        <div style={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
          <ChartErrorBoundary title={title} chartKey={chartKey}>
            {renderChartContent()}
          </ChartErrorBoundary>
        </div>
      );
    }

    // Initial load (no data yet) — show spinner
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spinner />
      </div>
    );
  };

  return (
    <div
      ref={visibilityRef}
      className={`chart-wrapper ${styles.cardWrapper}`}
      data-chart-key={chartKey}
    >
      <Card
        className={`${className ?? ''} ${onEdit ? styles.editableCard : ''}`.trim() || undefined}
        appearance="filled"
        style={effectiveChartType === 'kpi' ? { paddingTop: 0, paddingBottom: 0 } : undefined}
        onDoubleClick={onEdit ? () => onEdit(chartKey) : undefined}
      >
        <div
          className={`chart-actions ${styles.chartActions}`}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          {(onEdit || onDuplicate || onDelete) && (
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<MoreHorizontalRegular />}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Chart actions"
                  title="Chart actions"
                  className="chart-edit-btn"
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {onEdit && (
                    <MenuItem
                      icon={<EditRegular />}
                      onClick={(e) => { e.stopPropagation(); onEdit(chartKey); }}
                    >
                      Edit chart
                    </MenuItem>
                  )}
                  {onDuplicate && (
                    <MenuItem
                      icon={<CopyRegular />}
                      onClick={(e) => { e.stopPropagation(); onDuplicate(chartKey); }}
                    >
                      Duplicate chart
                    </MenuItem>
                  )}
                  {onDelete && (
                    <MenuItem
                      icon={<DeleteRegular />}
                      onClick={(e) => { e.stopPropagation(); onDelete(chartKey); }}
                    >
                      Delete chart
                    </MenuItem>
                  )}
                  {onSetAsThumbnail && effectiveChartType !== 'kpi' && effectiveChartType !== 'table' && (
                    <MenuItem
                      icon={<ImageRegular />}
                      onClick={(e) => { e.stopPropagation(); onSetAsThumbnail(chartKey); }}
                    >
                      Set as dashboard image
                    </MenuItem>
                  )}
                </MenuList>
              </MenuPopover>
            </Menu>
          )}

        </div>
        {!hideTitle && getCapabilities(effectiveChartType).titleInCard && (
          <div style={{ padding: '8px 16px', overflow: 'hidden' }}>
            <Body1 style={{ fontSize: '18px', fontWeight: '600', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }} title={title}>
              {title}
            </Body1>
          </div>
        )}
        <CardPreview style={effectiveChartType === 'kpi' ? { padding: 0 } : undefined}>
          {renderCardBody()}
        </CardPreview>
      </Card>
    </div>
  );
};